import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { PageHeader } from '@/lib/atsUi'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, notifications as initialNotifications } from '@/lib/mocks'
import type { Notification } from '@/lib/mocks'
import { DataList } from '@/components/ui/data-list'
import { Button } from '@/components/ui/button'
import { Inbox, AlertTriangle, CalendarClock, CircleCheck, Bell } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/notifications')({
  component: NotificationsPage,
})

function typeIcon(type: Notification['type']) {
  switch (type) {
    case 'approval':
      return <Inbox className="h-5 w-5 text-primary shrink-0" />
    case 'rpa':
      return <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
    case 'interview':
      return <CalendarClock className="h-5 w-5 text-blue-500 shrink-0" />
    case 'placement':
      return <CircleCheck className="h-5 w-5 text-green-600 shrink-0" />
    case 'system':
      return <Bell className="h-5 w-5 text-muted-foreground shrink-0" />
  }
}

function NotificationsPage() {
  const [items, setItems] = useState<Notification[]>(initialNotifications)

  function markAllRead() {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })))
    toast.success('All notifications marked as read')
  }

  function markRead(id: string) {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
  }

  return (
    <DashboardLayout title="Notifications" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Notifications"
        subtitle="Everything that needs your eye, newest first."
        action={
          <Button variant="outline" size="sm" onClick={markAllRead}>
            Mark all read
          </Button>
        }
      />

      <DataList
        items={items}
        getKey={(n) => n.id}
        pageSize={5}
        empty="You're all caught up."
        renderRow={(n) => (
          <button
            onClick={() => markRead(n.id)}
            className={cn(
              'w-full text-left flex items-start gap-4 px-5 py-4 hover:bg-muted/40 transition-colors',
              !n.read && 'bg-muted/20',
            )}
          >
            {/* Unread dot */}
            <span className="mt-1 shrink-0 w-2 flex justify-center">
              {!n.read && (
                <span className="inline-block h-2 w-2 rounded-full bg-primary" />
              )}
            </span>

            {/* Type icon */}
            <span className="mt-0.5">{typeIcon(n.type)}</span>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm leading-snug', !n.read ? 'font-semibold' : 'font-medium')}>
                {n.title}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{n.body}</p>
            </div>

            {/* Time */}
            <span className="text-xs text-muted-foreground shrink-0 mt-0.5">{n.time}</span>
          </button>
        )}
      />
    </DashboardLayout>
  )
}
